'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import About from '@/components/sections/About';
import {
  BookOpen,
  Hammer,
  Award,
  Rocket,
  Code2,
  Users,
  Gauge,
  ArrowRight,
  Mail,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const journeySteps = [
  {
    icon: BookOpen,
    label: 'Learning',
    period: '2019 — 2020',
    desc: 'Diving into HTML, CSS, JavaScript and foundational computer science.',
  },
  {
    icon: Hammer,
    label: 'Building',
    period: '2020 — 2022',
    desc: 'Shipping real products with React, Laravel and modern tooling.',
  },
  {
    icon: Award,
    label: 'Mastering',
    period: '2022 — 2024',
    desc: 'Architecting scalable full-stack systems and leading project teams.',
  },
  {
    icon: Rocket,
    label: 'Now',
    period: '2024 — Present',
    desc: 'Crafting polished products with Next.js, TypeScript and cloud-native stacks.',
  },
];

const philosophy = [
  {
    icon: Code2,
    title: 'Clean Code',
    desc: 'Readable, maintainable, and well-documented code that scales with your team and product.',
    tone: 'primary',
  },
  {
    icon: Users,
    title: 'User-First',
    desc: 'Every pixel and interaction is crafted with empathy for the people who will use it.',
    tone: 'accent',
  },
  {
    icon: Gauge,
    title: 'Performance',
    desc: 'Fast load times, smooth animations, and optimized queries — because speed is a feature.',
    tone: 'mint',
  },
];

export default function AboutPage() {
  return (
    <div className="about-page">
      <About />

      {/* My Journey */}
      <section className="about-page-journey">
        <div className="section-container">
          <motion.div
            className="section-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="section-label">My Journey</span>
            <h2 className="section-title">From Curious Beginner to Full-Stack Craftsman</h2>
            <p className="section-subtitle">
              A glimpse into the path that shaped the developer I am today.
            </p>
          </motion.div>

          <div className="about-page-stepper">
            <div className="about-page-stepper-line" />
            {journeySteps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.label}
                  className="about-page-step"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                >
                  <div className="about-page-step-icon">
                    <Icon size={22} />
                  </div>
                  <div className="about-page-step-body">
                    <span className="about-page-step-period">{step.period}</span>
                    <h3 className="about-page-step-title">{step.label}</h3>
                    <p className="about-page-step-desc">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tech Philosophy */}
      <section className="about-page-philosophy">
        <div className="section-container">
          <motion.div
            className="section-heading"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
          >
            <span className="section-label">Tech Philosophy</span>
            <h2 className="section-title">Principles that guide every line of code</h2>
            <p className="section-subtitle">
              Technology changes fast, but these values stay constant in my work.
            </p>
          </motion.div>

          <div className="about-page-philo-grid">
            {philosophy.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  className={`about-page-philo-card about-page-philo-${p.tone}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.12 }}
                >
                  <div className="about-page-philo-icon">
                    <Icon size={24} />
                  </div>
                  <h3 className="about-page-philo-title">{p.title}</h3>
                  <p className="about-page-philo-desc">{p.desc}</p>
                  <div className="about-page-philo-glow" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Let's Connect CTA */}
      <section className="about-page-cta-section">
        <div className="section-container">
          <motion.div
            className="about-page-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="about-page-cta-bg" />
            <div className="about-page-cta-content">
              <span className="about-page-cta-label">Let&apos;s Connect</span>
              <h2 className="about-page-cta-title">
                Have a project in mind? I&apos;d love to hear about it.
              </h2>
              <p className="about-page-cta-desc">
                Whether you&apos;re starting something new or leveling up an existing product,
                I&apos;m available to help turn your ideas into polished, performant software.
              </p>
              <div className="about-page-cta-actions">
                <Link href="/contact" className="btn btn-primary">
                  <Mail size={18} />
                  Get in Touch
                </Link>
                <Link href="/appointment" className="btn btn-outline">
                  Book a Call
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
